import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { useTheme } from "../../context/ThemeContext"; // 👈 Import
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  limit
} from "firebase/firestore";

export default function AddContact({ onChatCreated }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentUserId = auth.currentUser?.uid;
  const { isDarkMode } = useTheme(); // 👈 Use Theme

  // Auto-search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (!search.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const q = query(
          collection(db, "users"),
          where("username", ">=", search),
          where("username", "<=", search + "\uf8ff"),
          limit(10)
        );

        const snap = await getDocs(q);
        const found = snap.docs
          .filter((doc) => doc.id !== currentUserId)
          .map((doc) => ({ id: doc.id, ...doc.data() }));

        setResults(found);
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [search, currentUserId]);

  const startConversation = async (user) => {
    const members = [currentUserId, user.id].sort();
    const convId = members.join("_");

    try {
      await setDoc(doc(db, "conversations", convId), {
        members,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastMessageTimestamp: new Date(),
        unreadCounts: {
          [currentUserId]: 0,
          [user.id]: 0
        },
        lastReadTimestamp: {
          [currentUserId]: new Date(),
          [user.id]: new Date()
        }
      });

      onChatCreated({
        uid: user.id,
        username: user.username || "Unknown",
        photoURL: user.photoURL,
        convId,
      });
      setSearch(""); // Clear search
      setResults([]);
    } catch (err) {
      console.error("Failed to create conversation:", err);
    }
  };

  return (
    <div className="relative w-full z-50">
      <div className="relative">
        <input
          type="text"
          className={`w-full rounded-full px-4 py-2 pl-10 outline-none focus:ring-2 focus:ring-blue-400 transition-all font-medium ${isDarkMode ? 'bg-white/10 text-white border border-white/10 placeholder-gray-400' : 'bg-gray-100 text-black border border-transparent placeholder-gray-600'}`}
          style={{ color: isDarkMode ? 'white' : 'black' }}
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <svg
          className={`absolute left-3 top-2.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>

        {loading && (
          <div className="absolute right-3 top-2.5">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {/* Dropdown Results */}
      {results.length > 0 && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100 animate-fadeIn">
          <ul className="max-h-60 overflow-y-auto custom-scrollbar">
            {results.map((user) => (
              <li
                key={user.id}
                onClick={() => startConversation(user)}
                className="px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center gap-3 transition-colors border-b last:border-none border-gray-50"
              >
                {user.photoURL ? (
                  <img src={user.photoURL} alt={user.username} className="w-8 h-8 rounded-full object-cover border border-indigo-100" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-xs">
                    {user.username?.[0]?.toUpperCase()}
                  </div>
                )}
                <span className="text-gray-700 font-medium">{user.username}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No results state (optional, only if search has value) */}
      {search.trim() && !loading && results.length === 0 && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-lg p-4 text-center text-gray-500 text-sm border border-gray-100">
          No users found.
        </div>
      )}
    </div>
  );
}
